// excel.ts — 화면별 엑셀 내보내기 시트 빌더(순수 로직).
// 금액 계산은 data.ts 의 lineCalc/quoteTotals 를 그대로 사용한다(공식 단일 출처).
import {
  adminCost,
  cncBreakdown,
  cncPnL,
  processColumnTotal,
  processColumns,
  treatmentColumnTotal,
  treatmentCost,
  treatmentColumns,
  processCost,
} from "./cost/cncBreakdown";
import {
  type Customer,
  type MonthlyPoint,
  type Quote,
  type QuoteTotals,
  STATUS,
  customerNameFrom,
  lineCalc,
  quoteTotals,
} from "./data";
import type { CellValue, Sheet } from "./xlsx";

export const exportFileName = (prefix: string, date: string): string => `${prefix}_${date}.xlsx`;

const round1 = (n: number): number => Number(n.toFixed(1));

// ----- 견적 목록 (ListScreen — 현재 필터/정렬 결과를 그대로 내보낸다) -----
export type QuoteListRow = Quote & { t: QuoteTotals; custName: string };

export function quoteListSheet(rows: QuoteListRow[]): Sheet {
  return {
    name: "견적 목록",
    rows: [
      [
        "견적번호",
        "고객사",
        "프로젝트",
        "상태",
        "담당",
        "승인",
        "생성일",
        "수정일",
        "SOP",
        "내부원가(원)",
        "제출금액(원)",
        "영업이익(원)",
        "영업이익률(%)",
      ],
      ...rows.map((r) => [
        r.id,
        r.custName,
        r.project,
        STATUS[r.status].label,
        r.owner,
        r.approver,
        r.created,
        r.updated,
        r.sop,
        Math.round(r.t.internal),
        Math.round(r.t.submit),
        Math.round(r.t.profit),
        round1(r.t.profitRate),
      ]),
    ],
  };
}

// ----- 견적 상세 (DetailScreen — 요약 + 품목 스냅샷) -----
// procOrder = 원가마스터 공정 노출순서(호출부 주입). 미지정 시 통합 전 고정 규칙.
export function quoteDetailSheets(
  q: Quote,
  custName: string,
  procOrder?: readonly string[]
): Sheet[] {
  const t = quoteTotals(q.items);
  const summary: Sheet = {
    name: "견적 요약",
    rows: [
      ["견적번호", q.id],
      ["프로젝트", q.project],
      ["고객사", custName],
      ["상태", STATUS[q.status].label],
      ["담당", q.owner],
      ["승인", q.approver],
      ["생성일", q.created],
      ["수정일", q.updated],
      ["SOP", q.sop],
      ["제출 용도", q.purpose],
      [],
      ["내부 총원가(원)", Math.round(t.internal)],
      ["제출 금액(원)", Math.round(t.submit)],
      ["예상 영업이익(원)", Math.round(t.profit)],
      ["영업이익률(%)", round1(t.profitRate)],
    ],
  };
  const itemSheet: Sheet = {
    name: "품목",
    rows: [
      [
        "품목",
        "규격",
        "단위",
        "수량",
        "재료비(원)",
        "가공비(원)",
        "기타(원)",
        "마진율(%)",
        "내부단가(원)",
        "제출단가(원)",
        "제출금액(원)",
        "비고",
      ],
      ...q.items.map((it) => {
        const c = lineCalc(it);
        return [
          it.item,
          it.spec,
          it.unit,
          it.qty,
          it.mat,
          it.proc,
          it.etc,
          it.margin,
          Math.round(c.internal),
          Math.round(c.submit),
          Math.round(c.submitTotal),
          it.note,
        ];
      }),
    ],
  };
  const sheets: Sheet[] = [summary, itemSheet];

  // CNC 견적 — 분해 표 + 손익분석표 시트 추가(화면과 동일 집계 — cncBreakdown/cncPnL).
  // 재질/공법 라벨은 마스터 미연동 순수 모듈이라 selection.spec 만 사용(나머지 라벨 공란).
  // ponytail: 라벨 공란. 마스터명까지 필요하면 호출부가 labels 를 주입하도록 시그니처 확장.
  if (q.cnc.length) {
    const labels = q.cnc.map(() => ({ material: "", method: "" }));
    const bd = cncBreakdown(q.cnc, labels);
    // 가공비 컬럼 = 실제 공정명 합집합(화면 분해표와 동일 — processColumns).
    const procCols = processColumns(bd.rows, procOrder);
    // 재료비 세부 항목 컬럼 = 표면처리/열처리/기타 라인의 항목명 합집합(화면 분해표와 동일).
    const treatCols = treatmentColumns(bd.rows);
    const round = Math.round;
    sheets.push({
      name: "견적 상세(분해)",
      rows: [
        [
          "품목명",
          "연평균 물량(EA)",
          "공법",
          "규격",
          "재질",
          "제출중량(g)",
          "재료비",
          ...treatCols,
          "재료비계",
          ...procCols,
          "관리비",
          "금형비",
          "기타비용",
          "내부단가",
          "견적가",
          "이윤",
          "영업이익률(%)",
        ],
        ...bd.rows.map((r) => [
          r.itemName,
          round(r.avgVolume),
          r.method,
          r.spec,
          r.material,
          r.productWeightG,
          round(r.materialNet),
          ...treatCols.map((c) => round(treatmentCost(r, c))),
          round(r.materialTotal),
          ...procCols.map((c) => round(processCost(r, c))),
          round(adminCost(r)),
          round(r.mold),
          round(r.misc),
          round(r.internalCost),
          round(r.supplyPrice),
          round(r.profit),
          round1(r.profitRate),
        ]),
        [
          "합계",
          round(bd.totals.avgVolume),
          "",
          "",
          "",
          "",
          round(bd.totals.materialNet),
          ...treatCols.map((c) => round(treatmentColumnTotal(bd.rows, c))),
          round(bd.totals.materialTotal),
          ...procCols.map((c) => round(processColumnTotal(bd.rows, c))),
          round(adminCost(bd.totals)),
          round(bd.totals.mold),
          round(bd.totals.misc),
          round(bd.totals.internalCost),
          round(bd.totals.supplyPrice),
          round(bd.totals.profit),
          round1(bd.totals.profitRate),
        ],
      ],
    });

    // 손익분석표 — 아이템별 블록(물량/매출/이익/이익률) + 마지막 합계 블록(화면과 동일).
    const pnl = cncPnL(q.cnc, labels);
    type Pick = "volume" | "revenue" | "profit";
    const metrics: [string, Pick][] = [
      ["연간 물량(EA)", "volume"],
      ["매출액(원)", "revenue"],
      ["매출이익액(원)", "profit"],
    ];
    const block = (name: string, at: (i: number, pick: Pick) => number): CellValue[][] => {
      const total = (pick: Pick) => pnl.years.reduce((acc, _y, i) => acc + at(i, pick), 0);
      const rate = (rev: number, prof: number) => (rev > 0 ? round1((prof / rev) * 100) : 0);
      return [
        ...metrics.map(([label, pick]) => [
          name,
          label,
          ...pnl.years.map((_y, i) => round(at(i, pick))),
          round(total(pick)),
        ]),
        [
          name,
          "매출이익률(%)",
          ...pnl.years.map((_y, i) => rate(at(i, "revenue"), at(i, "profit"))),
          rate(total("revenue"), total("profit")),
        ],
      ];
    };
    sheets.push({
      name: "손익분석표",
      rows: [
        ["품목", "구분", ...pnl.years.map((y) => String(y)), "합계"],
        ...pnl.parts.flatMap((p) => block(p.name, (i, pick) => p.years[i]?.[pick] ?? 0)),
        ...block("합계", (i, pick) =>
          pnl.parts.reduce((acc, p) => acc + (p.years[i]?.[pick] ?? 0), 0)
        ),
      ],
    });
  }

  return sheets;
}

// ----- 통계 보고서 (ReportsScreen — KPI/월별/고객사별, 화면과 동일 집계) -----
export function reportSheets(
  quotes: Quote[],
  customers: Customer[],
  monthly: MonthlyPoint[]
): Sheet[] {
  const wt = quotes.map((q) => ({
    ...q,
    t: quoteTotals(q.items),
    custName: customerNameFrom(customers, q.customerId),
  }));
  const decided = wt.filter((q) => q.status === "won" || q.status === "lost");
  const winRate = decided.length
    ? (decided.filter((q) => q.status === "won").length / decided.length) * 100
    : 0;
  const wonAmount = wt.filter((q) => q.status === "won").reduce((a, q) => a + q.t.submit, 0);
  const avgMargin = wt.length ? wt.reduce((a, q) => a + q.t.profitRate, 0) / wt.length : 0;

  const kpi: Sheet = {
    name: "요약",
    rows: [
      ["지표", "값"],
      ["누적 견적(건)", quotes.length],
      ["수주율(%)", round1(winRate)],
      ["수주 금액(원)", Math.round(wonAmount)],
      ["평균 영업이익률(%)", round1(avgMargin)],
    ],
  };

  const monthlySheet: Sheet = {
    name: "월별 현황",
    rows: [
      ["월", "견적(건)", "수주(건)", "금액(원)"],
      ...monthly.map((p) => [p.m, p.quotes, p.won, p.amount]),
    ],
  };

  const byCust = new Map<string, { quotes: number; won: number; amount: number }>();
  for (const q of wt) {
    const v = byCust.get(q.custName) ?? { quotes: 0, won: 0, amount: 0 };
    v.quotes++;
    v.amount += q.t.submit;
    if (q.status === "won") v.won++;
    byCust.set(q.custName, v);
  }
  const custSheet: Sheet = {
    name: "고객사별",
    rows: [
      ["고객사", "견적 건수", "수주", "수주율(%)", "견적 총액(원)"],
      ...[...byCust.entries()]
        .sort((a, b) => b[1].amount - a[1].amount)
        .map(([name, v]) => [
          name,
          v.quotes,
          v.won,
          v.quotes ? Math.round((v.won / v.quotes) * 100) : 0,
          Math.round(v.amount),
        ]),
    ],
  };

  return [kpi, monthlySheet, custSheet];
}
